/**
 * Draws the menus onto the HUD canvas in the game's font: the title's menu straight over the title
 * scene (with a shadow, no panel: the logo and the figures are the scene's), every other screen on a
 * dark translucent panel over the playfield. Reads the model; changes nothing.
 */

import { textWidth } from '../font8';
import type { HudColours } from '../hud/panels';
import { CREDIT_LINES, type TextLine } from './content';
import { bar, bubble, centred, cover, footer, type MenuColours, menuColours, put, shadowed } from './draw';
import {
  CHANGE_PANE, CHANGE_PITCH, CHANGE_ROWS, CHANGE_X, CHANGE_Y0, CONTROL_HEADS, CONTROL_KEYS_X, CONTROL_NOTE_Y,
  CREDIT_PITCH, CREDIT_Y0, CX, HELP_Y, LABEL_X, LESS_X, NOTE_Y, PAD_Y, PLAY, rowYs, TITLE_SCALE, VALUE_X,
  VERSION_RIGHT,
} from './layout';
import type { MenuModel } from './model';
import { type MenuRow, WAITING } from './screens';
import { actionName } from './rebind';
import { wrapText } from './text';

export type { MenuColours } from './draw';

export function paint(ctx: CanvasRenderingContext2D, m: MenuModel, colours: HudColours | MenuColours): void {
  const screen = m.screen;
  if (!screen) return;
  const c = menuColours(colours);
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  switch (screen) {
    case 'title': title(ctx, m, c); break;
    case 'pause': pause(ctx, m, c); break;
    case 'options': options(ctx, m, c); break;
    case 'controls': controls(ctx, m, c); break;
    case 'credits': credits(ctx, m, c); break;
    case 'changes': changes(ctx, m, c); break;
  }
  ctx.restore();
}

function title(ctx: CanvasRenderingContext2D, m: MenuModel, c: MenuColours): void {
  const rows = m.rows();
  const ys = rowYs('title', rows.length);
  rows.forEach((r, i) => {
    const y = ys[i] ?? 0;
    const chosen = i === m.cursor;
    if (r.id === 'changes') {
      // The version, bottom right; NEW above it until this version's changes have been opened.
      const x = VERSION_RIGHT - textWidth(r.label);
      if (r.isNew) shadowed(ctx, 'NEW', VERSION_RIGHT - textWidth('NEW'), y - 12, c.accent, c);
      shadowed(ctx, r.label, x, y, chosen ? c.accent : c.dim, c);
      if (chosen) bubble(ctx, x - 12, y, c, 1, true);
      return;
    }
    const w = textWidth(r.label, TITLE_SCALE);
    const x = Math.round(CX - w / 2);
    shadowed(ctx, r.label, x, y, chosen ? c.accent : c.text, c, TITLE_SCALE);
    if (chosen) {
      bubble(ctx, x - 24, y, c, TITLE_SCALE, true);
    }
  });
}

/** A list row: the cursor, the label, and its value if it has one. */
function listRow(
  ctx: CanvasRenderingContext2D, r: MenuRow, y: number, chosen: boolean, c: MenuColours, valueX: number,
): void {
  if (chosen) bubble(ctx, LABEL_X - 16, y, c);
  put(ctx, r.label, LABEL_X, y, chosen ? c.accent : r.disabled ? c.dim : c.text);
  if (r.value === undefined) return;
  let x = valueX;
  if (r.bar !== undefined) {
    bar(ctx, r.bar, x, y, r.disabled ? c.dim : c.p2, c.dim);
    x += 68;
  }
  put(ctx, r.value, x, y, r.disabled ? c.dim : r.warn ? c.warn : c.p2);
  if (chosen && r.kind === 'option' && !r.disabled) {
    put(ctx, '<', LESS_X, y, c.accent);
    put(ctx, '>', x + textWidth(r.value) + 8, y, c.accent);
  }
}

function pause(ctx: CanvasRenderingContext2D, m: MenuModel, c: MenuColours): void {
  cover(ctx, 'PAUSED', c);
  const rows = m.rows();
  const ys = rowYs('pause', rows.length);
  rows.forEach((r, i) => {
    const y = ys[i] ?? 0;
    const x = Math.round(CX - textWidth(r.label) / 2);
    const chosen = i === m.cursor;
    if (chosen) bubble(ctx, x - 16, y, c);
    put(ctx, r.label, x, y, chosen ? c.accent : c.text);
  });
  footer(ctx, 'P CONTINUES   Q QUITS TO THE TITLE', c);
}

function options(ctx: CanvasRenderingContext2D, m: MenuModel, c: MenuColours): void {
  cover(ctx, 'OPTIONS', c);
  const rows = m.rows();
  const ys = rowYs('options', rows.length);
  rows.forEach((r, i) => listRow(ctx, r, ys[i] ?? 0, i === m.cursor, c, VALUE_X));
  wrapText(m.help(), 52).slice(0, 2).forEach((line, i) => centred(ctx, line, HELP_Y + i * 10, c.dim));
  const note = m.noteText();
  if (note) centred(ctx, note, NOTE_Y, c.accent);
  footer(ctx, 'UP DOWN CHOOSE   LEFT RIGHT CHANGE   ESC BACK', c);
}

const PAD_LINES: readonly (readonly [string, string])[] = [
  ['STICK OR D-PAD', 'MOVE'], ['A', 'JUMP'], ['B OR X', 'BLOW'], ['START', 'PAUSE'],
];

function controls(ctx: CanvasRenderingContext2D, m: MenuModel, c: MenuColours): void {
  cover(ctx, 'CONTROLS', c);
  put(ctx, 'PLAYER 1', LABEL_X - 16, CONTROL_HEADS[0], c.p1);
  put(ctx, 'PLAYER 2', LABEL_X - 16, CONTROL_HEADS[1], c.p2);
  const rows = m.rows();
  const ys = rowYs('controls', rows.length);
  // The waiting row pulses between two colours, held still under REDUCE FLASHING.
  const flash = m.store.get().display.reduceFlashing || Math.floor(m.frame / 12) % 2 === 0;
  rows.forEach((r, i) => {
    const waiting = r.kind === 'key' && r.value === WAITING;
    listRow(ctx, waiting ? { ...r, value: '' } : r, ys[i] ?? 0, i === m.cursor, c, CONTROL_KEYS_X);
    if (waiting) put(ctx, WAITING, CONTROL_KEYS_X, ys[i] ?? 0, flash ? c.accent : c.text);
  });
  const l = m.listening;
  const note = l ? `KEY FOR ${actionName(l.player, l.action)}?   ESC CANCELS` : m.noteText();
  if (note) centred(ctx, note, CONTROL_NOTE_Y, c.accent);
  put(ctx, 'GAMEPAD', LABEL_X - 16, PAD_Y, c.dim);
  PAD_LINES.forEach(([what, does], i) => {
    put(ctx, what, LABEL_X, PAD_Y + 14 + i * 12, c.text);
    put(ctx, does, CONTROL_KEYS_X, PAD_Y + 14 + i * 12, c.p2);
  });
  centred(ctx, 'ALWAYS: P PAUSE   Q QUIT WHEN PAUSED   F FRAME RATE', PAD_Y + 72, c.dim);
  footer(ctx, l ? 'PRESS THE NEW KEY' : 'ENTER CHANGES A KEY   ESC BACK', c);
}

function textLine(ctx: CanvasRenderingContext2D, t: TextLine, y: number, c: MenuColours, x?: number): void {
  const colour = t.role === 'head' ? c.p1 : t.role === 'accent' ? c.accent : t.role === 'dim' ? c.dim : c.text;
  if (x !== undefined) {
    put(ctx, t.text, x, y, colour);
    return;
  }
  const label = t.label ? `${t.label}  ` : '';
  const left = Math.round(CX - textWidth(label + t.text) / 2);
  if (label) put(ctx, label, left, y, c.dim);
  put(ctx, t.text, left + textWidth(label), y, colour);
}

/** The BACK row at the foot of the credits and the change list. */
function backRow(ctx: CanvasRenderingContext2D, m: MenuModel, screen: 'credits' | 'changes', c: MenuColours): void {
  const y = rowYs(screen, 1)[0] ?? 0;
  const x = Math.round(CX - textWidth('BACK') / 2);
  bubble(ctx, x - 16, y, c);
  put(ctx, 'BACK', x, y, m.cursor === 0 ? c.accent : c.text);
}

function credits(ctx: CanvasRenderingContext2D, m: MenuModel, c: MenuColours): void {
  cover(ctx, 'CREDITS', c);
  CREDIT_LINES.forEach((t, i) => textLine(ctx, t, CREDIT_Y0 + i * CREDIT_PITCH, c));
  backRow(ctx, m, 'credits', c);
}

function changes(ctx: CanvasRenderingContext2D, m: MenuModel, c: MenuColours): void {
  cover(ctx, 'CHANGES', c);
  const lines = m.changeList();
  lines.slice(m.scroll, m.scroll + CHANGE_ROWS)
    .forEach((t, i) => textLine(ctx, t, CHANGE_Y0 + i * CHANGE_PITCH, c, CHANGE_X));
  if (lines.length > CHANGE_ROWS) {
    // The scroll bar: the track, and the thumb for the lines in view.
    const x = PLAY.x + PLAY.w - 14;
    const h = CHANGE_PANE.h - 4;
    const thumb = Math.max(8, Math.round((h * CHANGE_ROWS) / lines.length));
    const top = Math.round(((h - thumb) * m.scroll) / (lines.length - CHANGE_ROWS));
    ctx.fillStyle = c.dim;
    ctx.fillRect(x + 1, CHANGE_Y0, 1, h);
    ctx.fillStyle = c.p2;
    ctx.fillRect(x, CHANGE_Y0 + top, 3, thumb);
  }
  backRow(ctx, m, 'changes', c);
  footer(ctx, 'UP DOWN SCROLL   LEFT RIGHT PAGE   ESC BACK', c);
}
